import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import {
    HeadingText30,
    HeadingText50,
    Modal,
    BodyTextSmall,
    Icon,
    ComponentTextSmall,
    pxToRem,
} from '@modernhealth/vital-components';

import { filterNullish } from '@modernhealth/filter-nullish';
import React, { useState } from 'react';
import { MobileAppInformationModal } from 'src/shared/components/MobileAppInformationModal';
import { OptionRow } from './OptionRow';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { EventCalendarModalContent } from './EventCalendarModalContent';
import {
    useDailyPauseReminderCalendarUrls,
    DailyPauseCalendarUrls,
} from 'src/modules/scheduledContent/hooks/useDailyPauseReminderCalendarUrls';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'src/config/rootStore';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { CleverTapProxyNotificationCategories } from 'src/modules/user/models/user';
import { useUpdateNotificationPreferencesMutation } from '../documents/updateNotificationPreferencesMutation.generated';
import { captureException } from 'src/shared/helpers/captureException';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { analyticsClient, EventType } from 'src/shared/services/analytics';

interface NotificationsModalProps {
    isVisible: boolean;
    onClose: () => void;
}

export const NotificationsModal: React.FC<NotificationsModalProps> = ({ isVisible, onClose }) => {
    const { t } = useTranslation();
    const { notificationPreferences } = useSelector(getUserInformation);
    const [mobileAppModalVisible, setMobileAppModalVisible] = useState(false);
    const [calendarEventActionsVisible, setCalendarEvenActionsVisible] = useState(false);
    const { loading, handleReminderCreation } = useDailyPauseReminderCalendarUrls();
    const {
        releaseDiginShowDailyPauseCalendarRemindersWeb,
        releaseDiginShowDailyPauseNotificationsWeb,
    } = useFlags();
    const dispatch = useDispatch<Dispatch>();
    const [updateNotificationPreferencesMutation, { loading: updatingNotificationPreferences }] =
        useUpdateNotificationPreferencesMutation();

    if (mobileAppModalVisible) {
        return (
            <MobileAppInformationModal
                isVisible={mobileAppModalVisible}
                onModalClose={() => setMobileAppModalVisible(false)}
            />
        );
    }

    const onModalClose = () => {
        onClose();
        setCalendarEvenActionsVisible(false);
    };

    const handleCalendarButtonClick = (calendarUrlType: DailyPauseCalendarUrls) => {
        handleReminderCreation(calendarUrlType);
        setCalendarEvenActionsVisible(false);
    };

    const options = filterNullish([
        releaseDiginShowDailyPauseNotificationsWeb
            ? {
                  title: 'dailyPause:additionalOptions.email_reminders.title',
                  description: 'dailyPause:additionalOptions.email_reminders.description',
                  onAction: async () => {
                      const newNotificationPreferences = {
                          ...notificationPreferences,
                          [CleverTapProxyNotificationCategories.DailyPauseRemindersEmail]:
                              !notificationPreferences.dailyPauseRemindersEmail,
                      };

                      dispatch.user.saveUserInfo({
                          notificationPreferences: newNotificationPreferences,
                      });

                      analyticsClient.track(
                          'Daily Pause: reminders email current setting',
                          EventType.API,
                          {
                              page_name: 'notifications-and-reminders-modal',
                              daily_pause_reminders_email:
                                  !notificationPreferences.dailyPauseRemindersEmail,
                          },
                      );

                      try {
                          await updateNotificationPreferencesMutation({
                              variables: {
                                  notificationPreferences: newNotificationPreferences,
                              },
                          });
                      } catch (err) {
                          captureException(err);

                          toastNotificationsService.renderErrorToast(config.messages.genericError);

                          dispatch.user.saveUserInfo({
                              notificationPreferences: {
                                  ...notificationPreferences,
                                  [CleverTapProxyNotificationCategories.DailyPauseRemindersEmail]:
                                      notificationPreferences.dailyPauseRemindersEmail,
                              },
                          });
                      }
                  },
                  switchValue: notificationPreferences.dailyPauseRemindersEmail,
                  disabled: updatingNotificationPreferences,
              }
            : null,
        releaseDiginShowDailyPauseCalendarRemindersWeb
            ? {
                  title: 'dailyPause:additionalOptions.calendar_reminders.title',
                  description: 'dailyPause:additionalOptions.calendar_reminders.description',
                  onAction: () => {
                      analyticsClient.trackClick('Daily Pause: open add to calendar modal');
                      setCalendarEvenActionsVisible(true);
                  },
              }
            : null,
    ]);

    return (
        <Modal isVisible={isVisible} onCloseButtonClick={onModalClose} noPaddings maxWidth={576}>
            {!calendarEventActionsVisible ? (
                <SettingsWrapper>
                    <HeadingText50 marginBottom={16}>
                        {t('dailyPause:notificationsModal.title')}
                    </HeadingText50>
                    <BodyTextSmall marginBottom={40}>
                        {t('dailyPause:additionalOptions.use_or_download')}{' '}
                        <Link onClick={() => setMobileAppModalVisible(true)}>
                            <ComponentTextSmall css={{ display: 'inline' }} fontColor="blue55">
                                {t('dailyPause:additionalOptions.mobile_app')}
                            </ComponentTextSmall>
                        </Link>
                    </BodyTextSmall>
                    <HeadingText30 marginBottom={26}>
                        {t('dailyPause:daily_pause.title')}
                    </HeadingText30>
                    {options.map(({ title, ...props }) => (
                        <OptionRow {...props} title={title} key={`notification_option_${title}`} />
                    ))}
                </SettingsWrapper>
            ) : (
                <CalendarEventsWrapper>
                    <IconContainer
                        onClick={() => {
                            setCalendarEvenActionsVisible(false);
                        }}
                    >
                        <Icon name="arrow-left" />
                    </IconContainer>
                    <EventCalendarModalContent
                        handleCalendarButtonClick={calendarUrlType => {
                            if (calendarUrlType === 'googleCalendarUrl') {
                                analyticsClient.trackClick('Daily Pause: add to google calendar');
                            }

                            if (calendarUrlType === 'icsUrl') {
                                analyticsClient.trackClick(
                                    'Daily Pause: add to local calendar (.ics)',
                                );
                            }

                            handleCalendarButtonClick(calendarUrlType);
                        }}
                        loading={loading}
                    />
                </CalendarEventsWrapper>
            )}
        </Modal>
    );
};

const SettingsWrapper = styled.div({
    padding: pxToRem(76, 38, 78),
});

const CalendarEventsWrapper = styled(SettingsWrapper)({
    paddingTop: pxToRem(36),
});

const Link = styled('a')({
    cursor: 'pointer',
    textDecoration: 'underline',

    '&:hover': {
        textDecoration: 'underline',
    },
});

const IconContainer = styled.div({
    marginTop: pxToRem(8),
    cursor: 'pointer',
});
